import React from 'react';

/**
 * The simplest possible pagination component.
 * No React Router, no state management, no event handlers.
 * Just plain HTML links with URL parameters.
 */
const SimplestPossiblePagination = ({ currentPage, totalPages }) => {
  // Don't render if there's only one page
  if (totalPages <= 1) {
    return null;
  }
  
  // Generate URL for a page
  const getPageUrl = (page) => {
    // Create a new URL object based on the current URL
    const url = new URL(window.location.href);
    
    // If we're going to page 1, remove the page parameter entirely
    // This ensures the default URL doesn't have a page parameter
    if (page === 1) {
      url.searchParams.delete('page');
    } else {
      // Set the page parameter for other pages
      url.searchParams.set('page', page);
    }
    
    // Return the URL as a string
    return url.toString();
  };
  
  // Generate an array of page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    // Always show first page
    pageNumbers.push(1);
    
    // Show ellipsis if needed
    if (currentPage > 3) {
      pageNumbers.push('...');
    }
    
    // Show pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i > 1 && i < totalPages) {
        pageNumbers.push(i);
      }
    }
    
    // Show ellipsis if needed
    if (currentPage < totalPages - 2) {
      pageNumbers.push('...');
    }
    
    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };
  
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0', gap: '5px' }}>
      {/* First page link */}
      {currentPage === 1 ? (
        <span style={{ 
          padding: '8px 12px', 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: '4px', 
          cursor: 'not-allowed', 
          fontSize: '14px', 
          color: '#6c757d', 
          opacity: 0.5 
        }}>
          First
        </span>
      ) : (
        <a 
          href={getPageUrl(1)} 
          style={{ 
            padding: '8px 12px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '4px', 
            cursor: 'pointer', 
            fontSize: '14px', 
            color: '#007bff', 
            textDecoration: 'none' 
          }}
        >
          First
        </a>
      )}
      
      {/* Previous page link */}
      {currentPage === 1 ? (
        <span style={{ 
          padding: '8px 12px', 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: '4px', 
          cursor: 'not-allowed', 
          fontSize: '14px', 
          color: '#6c757d', 
          opacity: 0.5 
        }}>
          Prev
        </span>
      ) : (
        <a 
          href={getPageUrl(currentPage - 1)} 
          style={{ 
            padding: '8px 12px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '4px', 
            cursor: 'pointer', 
            fontSize: '14px', 
            color: '#007bff', 
            textDecoration: 'none' 
          }}
        >
          Prev
        </a>
      )}
      
      {/* Page number links */}
      {getPageNumbers().map((number, index) => {
        if (number === '...') {
          return (
            <span 
              key={`ellipsis-${index}`} 
              style={{ 
                padding: '8px 12px', 
                fontSize: '14px', 
                color: '#6c757d' 
              }}
            >
              ...
            </span>
          );
        }
        
        if (number === currentPage) {
          return (
            <span 
              key={number} 
              style={{ 
                padding: '8px 12px', 
                backgroundColor: '#007bff', 
                border: '1px solid #007bff', 
                borderRadius: '4px', 
                cursor: 'default', 
                fontSize: '14px', 
                color: 'white' 
              }}
            >
              {number}
            </span>
          );
        }
        
        return (
          <a 
            key={number} 
            href={getPageUrl(number)} 
            style={{ 
              padding: '8px 12px', 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #dee2e6', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              fontSize: '14px', 
              color: '#007bff', 
              textDecoration: 'none' 
            }}
          >
            {number}
          </a>
        );
      })}
      
      {/* Next page link */}
      {currentPage === totalPages ? (
        <span style={{ 
          padding: '8px 12px', 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: '4px', 
          cursor: 'not-allowed', 
          fontSize: '14px', 
          color: '#6c757d', 
          opacity: 0.5 
        }}>
          Next
        </span>
      ) : (
        <a 
          href={getPageUrl(currentPage + 1)} 
          style={{ 
            padding: '8px 12px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '4px', 
            cursor: 'pointer', 
            fontSize: '14px', 
            color: '#007bff', 
            textDecoration: 'none' 
          }}
        >
          Next
        </a>
      )}
      
      {/* Last page link */}
      {currentPage === totalPages ? (
        <span style={{ 
          padding: '8px 12px', 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: '4px', 
          cursor: 'not-allowed', 
          fontSize: '14px', 
          color: '#6c757d', 
          opacity: 0.5 
        }}>
          Last
        </span>
      ) : (
        <a 
          href={getPageUrl(totalPages)} 
          style={{ 
            padding: '8px 12px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '4px', 
            cursor: 'pointer', 
            fontSize: '14px', 
            color: '#007bff', 
            textDecoration: 'none' 
          }}
        >
          Last
        </a>
      )}
    </div>
  );
};

export default SimplestPossiblePagination;
